import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AuthGuard from '../components/AuthGuard';
import { api } from '../lib/api';

interface IdCardApp {
  name: string;
  id_number: string;
  gender: string;
  ethnicity: string;
  birth_date: string;
  address: string;
  front_image_url: string;
  back_image_url: string;
  card_progress: number;
  status: string;
}

export default function NewIdCardResult() {
  const navigate = useNavigate();
  const [app, setApp] = useState<IdCardApp | null>(null);
  const apiBase = ((import.meta as any).env?.VITE_NEXT_PUBLIC_IMAGE_URL ?? '') as string;

  const resolveImg = (u: string | null) => {
    if (!u) return null;
    if (u.startsWith('http')) return u;
    try { const origin = new URL(apiBase).origin; return `${origin}${u}`; } catch { return u; }
  };

  useEffect(() => {
    api.get('/id-card/status').then(({ data }) => {
      if (!data.has_applied) {
        navigate('/new-id-card', { replace: true });
        return;
      }
      setApp(data.application);
    }).catch(() => { });
  }, []);

  const progress = app?.card_progress ?? 4;

  const stepStatus = (stepNum: number): 'done' | 'progress' | 'pending' => {
    if (stepNum < progress) return 'done';
    if (stepNum === progress) return 'progress';
    return 'pending';
  };

  const steps = [
    {
      icon: '/images/new_id_heyan.png',
      num: '01',
      title: '身份核验完成',
      desc: '身份信息核验通过',
      status: stepStatus(1),
    },
    {
      icon: '/images/new_id_wancheng.png',
      num: '02',
      title: '制证完成',
      desc: '身份证已制作完成',
      status: stepStatus(2),
    },
    {
      icon: '/images/new_id_lingqudian.png',
      num: '03',
      title: '已到领取点',
      desc: '身份证已送达指定领取点',
      status: stepStatus(3),
    },
    {
      icon: '/images/new_id_lingqu.png',
      num: '04',
      title: '等待本人领取',
      desc: '请携带本人身份证原件前往领取点',
      status: stepStatus(4),
    },
    {
      icon: '/images/new_id_yiloing.png',
      num: '05',
      title: '已领取',
      desc: '身份证领取成功',
      status: stepStatus(5),
    },
  ];

  const statusBadge = (status: 'done' | 'progress' | 'pending') => {
    if (status === 'done') {
      return (
        <div className="flex items-center gap-1 bg-[#eff4ea] rounded-2xl px-2 py-[5px] border border-[#33a645]">
          <img src="/images/new_id_yiwancheng.png" alt="" className="w-4 h-4" />
          <span className="text-xs text-green-600 font-medium">已完成</span>
        </div>
      );
    }
    if (status === 'progress') {
      return (
        <div className="flex items-center gap-1 bg-[#fce9e5] rounded-2xl px-2 py-[5px] border border-[#d19d19]">
          <img src="/images/new_id_jixingzhong.png" alt="" className="w-4 h-4" />
          <span className="text-xs text-orange-600 font-medium">进行中</span>
        </div>
      );
    }
    return (
      <div className="flex items-center gap-1 bg-[#eff4ea] rounded-2xl px-2 py-[5px] border border-[#d1d5db]">
        <img src="/images/new_id_diain.png" alt="" className="w-4 h-4 opacity-50" />
        <span className="text-xs text-gray-400 font-medium">未完成</span>
      </div>
    );
  };

  const [cardIndex, setCardIndex] = useState(0);
  const dragRef = useRef({ startX: 0, dragging: false });

  const handleDragStart = (clientX: number) => {
    dragRef.current = { startX: clientX, dragging: true };
  };
  const handleDragEnd = (clientX: number) => {
    if (!dragRef.current.dragging) return;
    dragRef.current.dragging = false;
    const diff = dragRef.current.startX - clientX;
    if (diff > 40) setCardIndex(i => Math.min(i + 1, 1));
    else if (diff < -40) setCardIndex(i => Math.max(i - 1, 0));
  };

  if (!app) return <div className="max-w-md mx-auto min-h-screen bg-[#8b1a1a]"><AuthGuard /></div>;

  return (
    <div className="max-w-md mx-auto min-h-screen pb-6"
      style={{
        backgroundImage: 'url(/images/new_id_beijing.png)',
        backgroundSize: '100% auto',
        backgroundRepeat: 'no-repeat',
        backgroundColor: '#8b1a1a',
      }}>
      <AuthGuard />

      {/* Back Button */}
      <button
        onClick={() => navigate(-1)}
        className="absolute top-4 left-3 z-10 w-8 h-8 flex items-center justify-center rounded-full bg-white/30 text-white"
      >
        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M15 18l-6-6 6-6" /></svg>
      </button>

      {/* ID Card Slider */}
      <div className="pt-[170px] px-3">
        <div className="relative">
          <img src="/images/new_id_kuang.png" alt="" className="w-full" />
          <div
            className="absolute inset-0 p-5 pt-7 pb-4 overflow-hidden cursor-grab active:cursor-grabbing select-none"
            onMouseDown={e => handleDragStart(e.clientX)}
            onMouseUp={e => handleDragEnd(e.clientX)}
            onMouseLeave={e => handleDragEnd(e.clientX)}
            onTouchStart={e => handleDragStart(e.touches[0].clientX)}
            onTouchEnd={e => handleDragEnd(e.changedTouches[0].clientX)}
          >
            <div
              className="flex gap-2 h-full transition-transform duration-300 ease-out"
              style={{ transform: `translateX(-${cardIndex * 100}%)` }}
            >
              <div className="w-full flex-shrink-0 flex items-center">
                <img src={resolveImg(app.front_image_url) || '/images/new_id_zheng.png'} alt="正面" className="w-full rounded-lg pointer-events-none" />
              </div>
              <div className="w-full flex-shrink-0 flex items-center">
                <img src={resolveImg(app.back_image_url) || '/images/new_id_fan.png'} alt="反面" className="w-full rounded-lg pointer-events-none" />
              </div>
            </div>

            <div className="flex justify-center gap-2 mt-1">
              <div className={`w-2 h-2 rounded-full transition-colors ${cardIndex === 0 ? 'bg-white' : 'bg-white/40'}`} />
              <div className={`w-2 h-2 rounded-full transition-colors ${cardIndex === 1 ? 'bg-white' : 'bg-white/40'}`} />
            </div>
          </div>
        </div>
      </div>

      {/* 领取流程 Section */}
      <div className="px-3 mt-4 relative">
        <div className="flex justify-center mb-3 absolute top-0 left-0 right-0 z-10">
          <img src="/images/new_id_liucehng.png" alt="领取流程" className="h-8" />
        </div>

        <div className="relative">
          <img src="/images/new_id_xiaokuang.png" alt="" className="w-full" />
          <div className="absolute inset-0 p-4 pt-[40px]">
            {steps.map((step, i) => (
              <div className="" key={i}>
                <div className="flex items-center gap-3">
                  {/* Step Icon */}
                  <img src={step.icon} alt="" className="w-10 h-10 flex-shrink-0" />

                  {/* Step Content */}
                  <div className="flex-1 min-w-0">
                    <div className="flex gap-3 items-start">
                      <span className="text-md text-[#b20000]">{step.num}</span>
                      <div className="flex flex-col items-baseline gap-0">
                        <span className="text-sm font-bold text-gray-800">{step.title}</span>
                        <span className="text-[11px] text-gray-400">{step.desc}</span>
                      </div>
                    </div>
                  </div>

                  {/* Status Badge */}
                  <div className="flex-shrink-0">
                    {statusBadge(step.status)}
                  </div>
                </div>

                {/* Divider with vertical line */}
                {i < steps.length - 1 && (
                  <div className="ml-5 border-l-2 border-[#ba0000] h-[25px]" />
                )}
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Bottom Button */}
      <div className="px-6 mt-4">
        <button className="w-full">
          <img src="/images/new_id_liji.png" alt="立即领取身份证" className="w-full" />
        </button>
      </div>

      {/* Customer Service */}
      <div className="flex justify-center items-center gap-1 mt-3 pb-4">
        <img src="/images/new_id_kefu.png" alt="" className="w-5 h-5" />
        <button onClick={() => navigate('/support')} className="text-xs text-gray-300">
          在线客服 &gt;
        </button>
      </div>
    </div>
  );
}
